import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { adminMatchesAPI, adminSeasonsAPI } from '../services/adminApi';
import styled from 'styled-components';
import AdminScoreModal from './AdminScoreModal';
import { MatchPadelGamesDetail } from './MatchPadelGamesDetail';

// Full viewport height container
const FullHeightContainer = styled.div`
  height: calc(100vh - 120px); // Account for header and padding
  display: flex;
  flex-direction: column;
  background: #f8fafc;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #043663 0%, #1e40af 100%);
  color: white;
  padding: 1.5rem;
  border-radius: 12px 12px 0 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  margin: 0 0 0.5rem 0;
  font-size: 1.75rem;
  font-weight: 700;
`;

const Subtitle = styled.p`
  margin: 0;
  opacity: 0.9;
  font-size: 1rem;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.875rem;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.5);
  }
  
  option {
    background: #043663;
    color: white;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.875rem;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }
  
  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

// Main content area with scroll
const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  background: white;
  border-radius: 0 0 12px 12px;
`;

// Division accordion container
const DivisionAccordion = styled.div`
  margin-bottom: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const DivisionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  cursor: pointer;
  background: ${props => props.$isExpanded ? '#f9fafb' : 'white'};
  transition: all 0.2s ease;
  border-bottom: ${props => props.$isExpanded ? '1px solid #e5e7eb' : 'none'};
  
  &:hover {
    background: #f9fafb;
  }
`;

const DivisionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const DivisionIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  background: ${props => props.$color || '#043663'};
`;

const DivisionDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const DivisionName = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
`;

const DivisionStats = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #6b7280;
`;

const StatItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ChevronIcon = styled.div`
  font-size: 1.25rem;
  color: #6b7280;
  transition: transform 0.2s ease;
  transform: ${props => props.$isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

// Teams container
const TeamsContainer = styled.div`
  padding: 0;
  background: #f9fafb;
`;

const TeamAccordion = styled.div`
  border-bottom: 1px solid #e5e7eb;
  
  &:last-child {
    border-bottom: none;
  }
`;

const TeamHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  cursor: pointer;
  background: ${props => props.$isExpanded ? '#f3f4f6' : 'white'};
  transition: all 0.2s ease;
  
  &:hover {
    background: #f3f4f6;
  }
`;

const TeamInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const TeamLogo = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
`;

const TeamDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const TeamName = styled.h4`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
`;

const TeamStats = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.125rem;
`;

// Matches container
const MatchesContainer = styled.div`
  padding: 0 1.5rem 1rem 1.5rem;
  background: #f3f4f6;
`;

const MatchItem = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ClickableMatchItem = styled(MatchItem)`
  cursor: pointer;
  &:hover {
    background: #f0f9ff;
    border-color: #043663;
  }
`;

const MatchHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const MatchTeams = styled.div`
  font-weight: 600;
  color: #1f2937;
  font-size: 0.9rem;
`;

const MatchHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MatchStatus = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => {
    switch (props.status) {
      case 'completed':
        return 'background: #dcfce7; color: #166534;';
      case 'in_progress':
        return 'background: #fef3c7; color: #92400e;';
      case 'scheduled':
        return 'background: #dbeafe; color: #1e40af;';
      case 'cancelled':
        return 'background: #fee2e2; color: #dc2626;';
      default:
        return 'background: #f3f4f6; color: #6b7280;';
    }
  }}
`;

const MatchOutcome = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  ${({ $result }) => {
    switch ($result) {
      case 'win':
        return 'background: #dcfce7; color: #15803d;';
      case 'loss':
        return 'background: #fee2e2; color: #b91c1c;';
      case 'draw':
        return 'background: #e0f2fe; color: #0369a1;';
      default:
        return 'background: #f3f4f6; color: #6b7280;';
    }
  }}
`;

const MatchDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
  font-size: 0.8rem;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: #374151;
  font-size: 0.7rem;
  text-transform: uppercase;
`;

const DetailValue = styled.span`
  color: #1f2937;
  font-size: 0.8rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #6b7280;
  font-size: 1rem;
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #dc2626;
  background: #fef2f2;
  border-radius: 8px;
  margin: 1rem;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #6b7280;
  font-size: 1rem;
`;

// Division colors
const DIVISION_COLORS = {
  'Beginner': '#10b981',      // Green
  'Beginner +': '#34d399',
  'Intermediate': '#3b82f6', // Blue
  'Intermediate +': '#60a5fa',
  'Advanced +': '#a78bfa',     // Purple (was Confirmed Advanced / 7.0–7.5)
  'Advanced': '#8b5cf6',       // Purple (6.0–6.5)
  'Confirmed Advanced': '#a78bfa', // legacy label
  'Elite': '#f59e0b',        // Orange
  'Pro': '#ef4444',          // Red
  'Master': '#6b7280',       // Gray
  'Champion': '#dc2626'      // Dark Red
};

const AdminMatchesByDivision = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedSeason, setSelectedSeason] = useState('');
  const [openDivisions, setOpenDivisions] = useState(new Set());
  const [openTeams, setOpenTeams] = useState(new Set());
  const [scoreModalMatch, setScoreModalMatch] = useState(null);
  const queryClient = useQueryClient();

  // Fetch seasons
  const { data: seasonsData } = useQuery(
    'adminSeasons',
    () => adminSeasonsAPI.getAll(),
    { staleTime: 300000 }
  );

  // Fetch matches data
  const { data: matchesData, isLoading, error } = useQuery(
    ['adminMatchesByDivision', statusFilter, selectedSeason],
    () => adminMatchesAPI.getAll({ 
      status: statusFilter || undefined,
      season_id: selectedSeason || undefined 
    }),
    {
      enabled: !!selectedSeason, // Only fetch when a season is selected
      staleTime: 30000,
      cacheTime: 300000,
    }
  );

  const toggleDivision = (divisionName) => {
    const newOpenDivisions = new Set(openDivisions);
    if (newOpenDivisions.has(divisionName)) {
      newOpenDivisions.delete(divisionName);
    } else {
      newOpenDivisions.add(divisionName);
    }
    setOpenDivisions(newOpenDivisions);
  };

  const toggleTeam = (teamKey) => {
    const newOpenTeams = new Set(openTeams);
    if (newOpenTeams.has(teamKey)) {
      newOpenTeams.delete(teamKey);
    } else {
      newOpenTeams.add(teamKey);
    }
    setOpenTeams(newOpenTeams);
  };

  // Show message if no season is selected
  if (!selectedSeason) {
    return (
      <FullHeightContainer>
        <Header>
          <Title>🏆 Matches by Division</Title>
          <Subtitle>View all matches organized by division and team</Subtitle>
          <FilterContainer>
            <SearchInput
              type="text"
              placeholder="Search divisions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FilterSelect
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
            >
              <option value="">Select Phase...</option>
              {seasonsData?.data?.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.name} {season.status === 'active' ? '(Active)' : season.status === 'completed' ? '(Completed)' : ''}
                </option>
              ))}
            </FilterSelect>
            <FilterSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="score_submitted">Pending Confirmation</option>
              <option value="completed">Completed</option>
              <option value="disputed">Disputed</option>
              <option value="cancelled">Cancelled</option>
            </FilterSelect>
          </FilterContainer>
        </Header>
        <ContentArea>
          <EmptyState>Please select a phase to view matches</EmptyState>
        </ContentArea>
      </FullHeightContainer>
    );
  }

  if (isLoading) {
    return (
      <FullHeightContainer>
        <Header>
          <Title>🏆 Matches by Division</Title>
          <Subtitle>View all matches organized by division and team</Subtitle>
          <FilterContainer>
            <SearchInput
              type="text"
              placeholder="Search divisions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FilterSelect
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
            >
              <option value="">Select Phase...</option>
              {seasonsData?.data?.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.name} {season.status === 'active' ? '(Active)' : season.status === 'completed' ? '(Completed)' : ''}
                </option>
              ))}
            </FilterSelect>
            <FilterSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="score_submitted">Pending Confirmation</option>
              <option value="completed">Completed</option>
              <option value="disputed">Disputed</option>
              <option value="cancelled">Cancelled</option>
            </FilterSelect>
          </FilterContainer>
        </Header>
        <ContentArea>
          <LoadingContainer>Loading matches...</LoadingContainer>
        </ContentArea>
      </FullHeightContainer>
    );
  }

  if (error) {
    return (
      <FullHeightContainer>
        <Header>
          <Title>🏆 Matches by Division</Title>
          <Subtitle>View all matches organized by division and team</Subtitle>
          <FilterContainer>
            <SearchInput
              type="text"
              placeholder="Search divisions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FilterSelect
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
            >
              <option value="">Select Phase...</option>
              {seasonsData?.data?.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.name} {season.status === 'active' ? '(Active)' : season.status === 'completed' ? '(Completed)' : ''}
                </option>
              ))}
            </FilterSelect>
            <FilterSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="score_submitted">Pending Confirmation</option>
              <option value="completed">Completed</option>
              <option value="disputed">Disputed</option>
              <option value="cancelled">Cancelled</option>
            </FilterSelect>
          </FilterContainer>
        </Header>
        <ContentArea>
          <ErrorContainer>Error loading matches: {error.message}</ErrorContainer>
        </ContentArea>
      </FullHeightContainer>
    );
  }

  // Group matches by division, then by team
  const matchesByDivision = {};
  const matches = matchesData?.data?.matches || [];

  console.log('🔍 AdminMatchesByDivision - Processing matches:', matches.length);
  console.log('🔍 Sample match data:', matches.slice(0, 3).map(m => ({
    id: m.id,
    division_name: m.division_name,
    division_level: m.division_level,
    team1_name: m.team1_name,
    team2_name: m.team2_name
  })));

  matches.forEach(match => {
    const divisionName = match.division_name || 'Unknown Division';
    
    console.log('🔍 Processing match:', {
      id: match.id,
      division_name: match.division_name,
      divisionName: divisionName,
      teams: `${match.team1_name} vs ${match.team2_name}`
    });
    
    if (!matchesByDivision[divisionName]) {
      matchesByDivision[divisionName] = {};
    }

    // Group by team1
    if (!matchesByDivision[divisionName][match.team1_name]) {
      matchesByDivision[divisionName][match.team1_name] = [];
    }
    matchesByDivision[divisionName][match.team1_name].push({ ...match, isTeam1: true });

    // Group by team2
    if (!matchesByDivision[divisionName][match.team2_name]) {
      matchesByDivision[divisionName][match.team2_name] = [];
    }
    matchesByDivision[divisionName][match.team2_name].push({ ...match, isTeam1: false });
  });

  // Filter divisions based on search term
  const filteredDivisions = Object.keys(matchesByDivision).filter(divisionName =>
    divisionName.toLowerCase().includes(searchTerm.toLowerCase())
  ).sort();

  console.log('🔍 Final division names being displayed:', filteredDivisions);
  console.log('🔍 All division names in matchesByDivision:', Object.keys(matchesByDivision));

  if (filteredDivisions.length === 0) {
    return (
      <FullHeightContainer>
        <Header>
          <Title>🏆 Matches by Division</Title>
          <Subtitle>View all matches organized by division and team</Subtitle>
          <FilterContainer>
            <SearchInput
              type="text"
              placeholder="Search divisions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FilterSelect
              value={selectedSeason}
              onChange={(e) => setSelectedSeason(e.target.value)}
            >
              <option value="">All Phases</option>
              {seasonsData?.data?.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.name}
                </option>
              ))}
            </FilterSelect>
            <FilterSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="score_submitted">Pending Confirmation</option>
              <option value="completed">Completed</option>
              <option value="disputed">Disputed</option>
              <option value="cancelled">Cancelled</option>
            </FilterSelect>
          </FilterContainer>
        </Header>
        <ContentArea>
          <EmptyState>No matches found for the selected criteria</EmptyState>
        </ContentArea>
      </FullHeightContainer>
    );
  }

  return (
    <FullHeightContainer>
      <Header>
        <Title>🏆 Matches by Division</Title>
        <Subtitle>View all matches organized by division and team</Subtitle>
        <FilterContainer>
          <SearchInput
            type="text"
            placeholder="Search divisions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FilterSelect
            value={selectedSeason}
            onChange={(e) => setSelectedSeason(e.target.value)}
          >
            <option value="">Select Phase...</option>
            {seasonsData?.data?.map((season) => (
              <option key={season.id} value={season.id}>
                {season.name} {season.status === 'active' ? '(Active)' : season.status === 'completed' ? '(Completed)' : ''}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </FilterSelect>
        </FilterContainer>
      </Header>

      <ContentArea>
        {filteredDivisions.map(divisionName => {
          const divisionTeams = matchesByDivision[divisionName];
          const isDivisionOpen = openDivisions.has(divisionName);
          
          // Calculate division stats
          const totalTeams = Object.keys(divisionTeams).length;
          
          // Count UNIQUE matches (each match appears in both team1 and team2 arrays, so we need to deduplicate)
          const uniqueMatches = new Set();
          const uniqueCompletedMatches = new Set();
          
          Object.values(divisionTeams).forEach(teamMatches => {
            teamMatches.forEach(match => {
              if (match.id) {
                uniqueMatches.add(match.id);
                if (match.status === 'completed') {
                  uniqueCompletedMatches.add(match.id);
                }
              }
            });
          });
          
          const totalMatches = uniqueMatches.size;
          const completedMatches = uniqueCompletedMatches.size;

          return (
            <DivisionAccordion key={divisionName}>
              <DivisionHeader 
                $isExpanded={isDivisionOpen}
                onClick={() => toggleDivision(divisionName)}
              >
                <DivisionInfo>
                  <DivisionIcon $color={DIVISION_COLORS[divisionName]}>
                    {divisionName.charAt(0)}
                  </DivisionIcon>
                  <DivisionDetails>
                    <DivisionName>{divisionName}</DivisionName>
                    <DivisionStats>
                      <StatItem>👥 {totalTeams} teams</StatItem>
                      <StatItem>⚽ {totalMatches} matches</StatItem>
                      <StatItem>✅ {completedMatches} completed</StatItem>
                    </DivisionStats>
                  </DivisionDetails>
                </DivisionInfo>
                <ChevronIcon $isExpanded={isDivisionOpen}>▼</ChevronIcon>
              </DivisionHeader>

              {isDivisionOpen && (
                <TeamsContainer>
                  {Object.keys(divisionTeams).sort().map(teamName => {
                    const teamMatches = divisionTeams[teamName];
                    const teamKey = `${divisionName}-${teamName}`;
                    const isTeamOpen = openTeams.has(teamKey);
                    
                    // Sort matches by date
                    const sortedMatches = teamMatches.sort((a, b) => {
                      const dateA = new Date(a.game_window_start || 0);
                      const dateB = new Date(b.game_window_start || 0);
                      return dateA - dateB;
                    });

                    return (
                      <TeamAccordion key={teamName}>
                        <TeamHeader 
                          $isExpanded={isTeamOpen}
                          onClick={() => toggleTeam(teamKey)}
                        >
                          <TeamInfo>
                            <TeamLogo>
                              {teamName.split(' ').map(word => word.charAt(0)).join('').slice(0, 2).toUpperCase()}
                            </TeamLogo>
                            <TeamDetails>
                              <TeamName>{teamName}</TeamName>
                              <TeamStats>
                                {sortedMatches.length} matches • 
                                {sortedMatches.filter(m => m.status === 'completed').length} completed
                              </TeamStats>
                            </TeamDetails>
                          </TeamInfo>
                          <ChevronIcon $isExpanded={isTeamOpen}>▼</ChevronIcon>
                        </TeamHeader>

                        {isTeamOpen && (
                          <MatchesContainer>
                            {sortedMatches.map((match, index) => (
                              <ClickableMatchItem
                                key={`${match.id}-${index}`}
                                onClick={() => setScoreModalMatch(match)}
                              >
                            {(() => {
                              const toNumber = (value) =>
                                value === null || value === undefined ? null : Number(value);

                              const myScores = [
                                toNumber(match.isTeam1 ? match.team1_score_set1 : match.team2_score_set1),
                                toNumber(match.isTeam1 ? match.team1_score_set2 : match.team2_score_set2),
                                toNumber(match.isTeam1 ? match.team1_score_set3 : match.team2_score_set3),
                              ];

                              const oppScores = [
                                toNumber(match.isTeam1 ? match.team2_score_set1 : match.team1_score_set1),
                                toNumber(match.isTeam1 ? match.team2_score_set2 : match.team1_score_set2),
                                toNumber(match.isTeam1 ? match.team2_score_set3 : match.team1_score_set3),
                              ];

                              const setComparisons = myScores.map((my, idx) => ({
                                my,
                                opp: oppScores[idx],
                              }));

                              const hasScoreData = setComparisons.some(
                                ({ my, opp }) => my !== null && opp !== null,
                              );

                              const setsWon = setComparisons.reduce((total, { my, opp }) => {
                                if (my === null || opp === null) return total;
                                return total + (my > opp ? 1 : 0);
                              }, 0);

                              const setsLost = setComparisons.reduce((total, { my, opp }) => {
                                if (my === null || opp === null) return total;
                                return total + (my < opp ? 1 : 0);
                              }, 0);

                              let resultType = null;
                              if (match.status === 'completed' && hasScoreData) {
                                if (setsWon > setsLost) resultType = 'win';
                                else if (setsWon < setsLost) resultType = 'loss';
                                else resultType = 'draw';
                              }

                              const shouldShowSet = (idx, my, opp) => {
                                if (my === null || opp === null) return false;
                                if (idx < 2) return true;
                                return my > 0 || opp > 0;
                              };

                              const formattedScore = match.status === 'completed' && hasScoreData
                                ? setComparisons
                                    .map(({ my, opp }, idx) =>
                                      shouldShowSet(idx, my, opp) ? `${my}-${opp}` : null,
                                    )
                                    .filter(Boolean)
                                    .join(', ')
                                : 'No score recorded';

                              const resultLabel =
                                resultType === 'win'
                                  ? `Win ${setsWon}-${setsLost}`
                                  : resultType === 'loss'
                                  ? `Loss ${setsWon}-${setsLost}`
                                  : resultType === 'draw'
                                  ? 'Draw'
                                  : null;

                              return (
                                <>
                                  <MatchHeader>
                                    <MatchTeams>
                                      {match.isTeam1 ? (
                                        <strong>{match.team1_name}</strong>
                                      ) : (
                                        match.team1_name
                                      )}{' '}
                                      vs{' '}
                                      {match.isTeam1 ? (
                                        match.team2_name
                                      ) : (
                                        <strong>{match.team2_name}</strong>
                                      )}
                                    </MatchTeams>
                                    <MatchHeaderRight>
                                      {resultLabel && (
                                        <MatchOutcome $result={resultType}>{resultLabel}</MatchOutcome>
                                      )}
                                      <MatchStatus status={match.status}>{match.status}</MatchStatus>
                                    </MatchHeaderRight>
                                  </MatchHeader>

                                  <MatchDetails>
                                    <DetailItem>
                                      <DetailLabel>Date & Time</DetailLabel>
                                      <DetailValue>
                                        {match.game_window_start
                                          ? new Date(match.game_window_start).toLocaleString()
                                          : 'TBD'}
                                      </DetailValue>
                                    </DetailItem>

                                    <DetailItem>
                                      <DetailLabel>Venue</DetailLabel>
                                      <DetailValue>{match.court_name || match.venue || 'TBD'}</DetailValue>
                                    </DetailItem>

                                    <DetailItem>
                                      <DetailLabel>Match #{index + 1}</DetailLabel>
                                      <DetailValue>
                                        {match.match_number || `Match ${index + 1}`}
                                      </DetailValue>
                                    </DetailItem>

                                    {match.status === 'completed' && (
                                      <DetailItem>
                                        <DetailLabel>Score</DetailLabel>
                                        <DetailValue>{formattedScore}</DetailValue>
                                      </DetailItem>
                                    )}
                                    <MatchPadelGamesDetail
                                      match={match}
                                      DetailItem={DetailItem}
                                      DetailLabel={DetailLabel}
                                      DetailValue={DetailValue}
                                    />
                                  </MatchDetails>
                                </>
                              );
                            })()}
                              </ClickableMatchItem>
                            ))}
                          </MatchesContainer>
                        )}
                      </TeamAccordion>
                    );
                  })}
                </TeamsContainer>
              )}
            </DivisionAccordion>
          );
        })}
      </ContentArea>

      <AdminScoreModal
        isOpen={!!scoreModalMatch}
        onClose={() => setScoreModalMatch(null)}
        match={scoreModalMatch}
        onSuccess={() => queryClient.invalidateQueries(['adminMatchesByDivision'])}
      />
    </FullHeightContainer>
  );
};

export default AdminMatchesByDivision;
